import BottomSpacer from "@/components/shared/BottomSpacer";
import { useAttachmentViewerState } from "@/zustand/AttachmentViewerState";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useControls } from "react-zoom-pan-pinch";
import { forwardRef, useState } from "react";
import { saveFile } from "@/lib/saveFile";
import { cn } from "@/lib/utils";

interface ViewerControlsProps {
  url: string;
  filename: string;
  extraControls?: React.ReactNode;
  /**
   * Immersive mode (video, controls faded out). The bar stays MOUNTED — its
   * measured height keeps the media centered in the same space — but is
   * invisible and inert until a tap brings it back.
   */
  hidden?: boolean;
}

// forwardRef so the dialog can measure the bar's height and reserve that much
// space for the media above it. The ref lands on the <footer>.
const ViewerControls = forwardRef<HTMLElement, ViewerControlsProps>(
  ({ url, filename, extraControls, hidden = false }, ref) => {
    const showNext = useAttachmentViewerState((state) => state.showNext);
    const showPrevious = useAttachmentViewerState(
      (state) => state.showPrevious,
    );
    const nextAttachment = useAttachmentViewerState(
      (state) => state.nextAttachment,
    );
    const previousAttachment = useAttachmentViewerState(
      (state) => state.previousAttachment,
    );
    const { zoomIn, zoomOut } = useControls();
    // Guards a re-tap while the fetch-to-blob download is in flight.
    const [downloading, setDownloading] = useState(false);
    async function handleDownload() {
      // Full media may not be loaded yet (url === ""); no-op until it is.
      if (!url || downloading) {
        return;
      }
      setDownloading(true);
      try {
        // Fetch the source to a Blob first — SW-intercepted for /sw-stream on
        // every engine (a raw anchor to a stream URL would save the SPA shell),
        // and cheap for a local blob: URL — then let saveFile pick the
        // platform-best save: native share sheet on iOS, silent anchor else.
        const response = await fetch(url);
        if (!response.ok) {
          return;
        }
        await saveFile(await response.blob(), filename);
      } catch {
        // Network/stream error — leave the button enabled for a retry.
      } finally {
        setDownloading(false);
      }
    }
    return (
      <>
        {showPrevious && !hidden && (
          <aside className="absolute left-0 top-0 flex h-full flex-col items-center justify-center">
            <button
              type="button"
              aria-label="Previous"
              onClick={previousAttachment}
              className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ChevronLeft className="size-8 bg-primary/20" />
            </button>
          </aside>
        )}
        {showNext && !hidden && (
          <aside className="absolute right-0 top-0 flex h-full flex-col items-center justify-center">
            <button
              type="button"
              aria-label="Next"
              onClick={nextAttachment}
              className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ChevronRight className="size-8 bg-primary/20" />
            </button>
          </aside>
        )}
        {/* Persistent toolbar (no auto-hide). z-10 so a zoomed image that
            extends behind it stays behind. Same frosted backdrop as the modal
            (see AttachmentViewerDialog), so it reads as part of the modal.
            data-no-swipe: a horizontal drag resting on/starting from the bar
            must not navigate the carousel (see swipeGuards.ts). */}
        <footer
          ref={ref}
          data-no-swipe=""
          aria-hidden={hidden}
          // `inert` (not just pointer-events-none) so the hidden bar's buttons
          // can't be reached by Tab either — aria-hidden + focusable would trap
          // keyboard users on invisible controls. Spread as an untyped attribute
          // because React's types here predate `inert`.
          {...(hidden ? ({ inert: "" } as Record<string, string>) : {})}
          className={cn(
            "absolute bottom-0 left-0 z-10 w-full bg-white/90 backdrop-blur-md transition-opacity duration-200 dark:bg-black/90",
            hidden && "pointer-events-none opacity-0",
          )}
        >
          {/* Bigger, more visible controls. Centered on mobile (thumb-friendly),
              right-aligned on desktop (lg+). Taller bar via py-5. */}
          <div className="flex w-full items-center justify-center gap-6 !px-4 !py-5 lg:justify-end lg:!px-8">
            <button
              type="button"
              aria-label="Download"
              onClick={handleDownload}
              disabled={!url || downloading}
              className="cursor-pointer opacity-90 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Download className="size-7" />
            </button>
            {extraControls}
            <div className="flex items-center gap-5">
              <button
                type="button"
                aria-label="Zoom in"
                onClick={() => zoomIn()}
                className="cursor-pointer opacity-90 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <ZoomIn className="size-7" />
              </button>
              <button
                type="button"
                aria-label="Zoom out"
                onClick={() => zoomOut()}
                className="cursor-pointer opacity-90 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <ZoomOut className="size-7" />
              </button>
            </div>
          </div>
          <BottomSpacer />
        </footer>
      </>
    );
  },
);
ViewerControls.displayName = "ViewerControls";

export default ViewerControls;
